/* eslint-disable no-console */
import { captureException } from '@surglogs-app/error-reporting'
import type { Config, IClinicContextStore } from '@surglogs-app/types'

import { services } from './services/analytics'
import { getUserData, isUserTrackable } from './services/UserService'

type InitAnalyticsToolsConfig = Omit<Config, 'onlyofficeUrl'> & {
  withSessionRecording?: boolean
}
type InitAnalyticsToolsOptions = {
  withSessionRecording?: boolean
}

export const initAnalyticsTools = (
  config: InitAnalyticsToolsConfig,
  opts: InitAnalyticsToolsOptions,
) => {
  try {
    if (window.Cypress || window.Playwright) return

    const release = config.appVersion || '0.0.1'

    services.forEach((s) => s.init({ config, release, ...opts }))
  } catch (error) {
    captureException(error)
  }
}

export const initAnalyticsToolsBootstrapHook = (
  config: InitAnalyticsToolsConfig,
  {
    auth,
    clinicContextStoreInstance,
  }: {
    auth: Auth
    clinicContextStoreInstance?: IClinicContextStore
  },
) => {
  try {
    if (window.Cypress || window.Playwright) return

    const release = config.appVersion || '0.0.1'
    const isDev =
      window.location.hostname.includes('dev') ||
      window.location.hostname.includes('review')

    services.forEach((s) => s.init({ config, release }))

    const updateAnalytics = () => {
      const user = auth.contextDataStore.getData()?.user
      const clinic = clinicContextStoreInstance?.getData()
      const clinicId = clinicContextStoreInstance?.getClinicId()
      const clinicData = clinic || (clinicId ? { id: clinicId } : null)

      const { capturingEnabled, recordingEnabled } = isUserTrackable({
        user,
        clinic: clinicData as typeof clinic,
      })

      if (user) {
        // always track users on devel env
        if (capturingEnabled || isDev) {
          services.forEach((s) => {
            s.enableCapturing?.()
            s.identifyUser({ user })
            s.setEventProperties?.({
              data: {
                clinic: clinicData,
                user: getUserData(user),
              },
            })
          })
        }

        if (recordingEnabled) {
          services.forEach((s) => s.enableSessionRecording?.())
        }
      } else {
        services.forEach((s) => s.disableCapturing?.())
        services.forEach((s) => s.disableSessionRecording?.())
      }
    }

    auth.contextDataStore.onChange(() => {
      updateAnalytics()
    })

    clinicContextStoreInstance?.onChange(() => {
      updateAnalytics()
    })
  } catch (error) {
    captureException(error)
  }
}
